/* PushEnablementRow.jsx — Calm operational affordance for enabling
 * browser push.
 *
 * Lives inside NotificationCenter, above the grouped notifications. Two
 * states:
 *   • idle           — single slate row with subtle violet "Enable" CTA
 *   • enabled        — quiet green-soft confirmation row with "Test"
 *                       and "Turn off" actions
 *
 * Discipline:
 *   • Never modal/interstitial. The bell is the only way to encounter it.
 *   • Dismissable — once declined, doesn't re-prompt for the session.
 *   • No marketing copy. Operational, calm, one-line.
 */

import React, { useEffect, useState } from "react";
import { BellRing, BellOff, Send, X, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import {
  currentPermission,
  getServerEnabled,
  isPushSupported,
  markPushDeclined,
  sendTestPush,
  subscribeToPush,
  unsubscribeFromPush,
  wasPushDeclined,
} from "../../lib/pushSubscription";
import { decidePushAffordance } from "../../lib/pushAffordance";

const SESSION_DISMISS_KEY = "cf:push-row-dismissed";

export const PushEnablementRow = () => {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState("default");
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [serverOn, setServerOn] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  /* Bootstrap — check support + server availability + current subscription. */
  useEffect(() => {
    let alive = true;
    (async () => {
      const ok = isPushSupported();
      setSupported(ok);
      if (!ok) return;
      const sOn = await getServerEnabled();
      if (!alive) return;
      setServerOn(sOn);
      setPermission(currentPermission());
      try { setDismissed(sessionStorage.getItem(SESSION_DISMISS_KEY) === "1"); } catch { /* noop */ }
      // Check if there's an active subscription on this device.
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        if (alive) setSubscribed(Boolean(sub));
      } catch { /* noop */ }
    })();
    return () => { alive = false; };
  }, []);

  /* Hide entirely when:
     • not supported by browser
     • server not configured for push
     • session dismissed in idle state
     • user previously denied OS permission AND hasn't reset it
  */
  const decision = decidePushAffordance({
    supported,
    serverOn,
    permission,
    subscribed,
    dismissed,
    declined: wasPushDeclined(),
  });
  if (decision === "hidden") return null;
  const isEnabled = decision === "enabled";

  const onEnable = async () => {
    setBusy(true);
    const result = await subscribeToPush();
    setBusy(false);
    if (result.ok) {
      setPermission("granted");
      setSubscribed(true);
      toast.success("Push enabled · We'll only notify you on operational signals.");
    } else if (result.reason === "denied") {
      toast.error("Browser blocked notifications. Enable them in browser settings to receive operational signals.");
      setPermission("denied");
    } else if (result.reason === "dismissed") {
      // user closed the system prompt without choosing
      try { sessionStorage.setItem(SESSION_DISMISS_KEY, "1"); } catch { /* noop */ }
      setDismissed(true);
    } else if (result.reason === "server_disabled") {
      setServerOn(false);
    } else {
      toast.error("Couldn't enable push. Try again from a different browser.");
    }
  };

  const onDismiss = () => {
    try { sessionStorage.setItem(SESSION_DISMISS_KEY, "1"); } catch { /* noop */ }
    markPushDeclined();
    setDismissed(true);
  };

  const onDisable = async () => {
    setBusy(true);
    await unsubscribeFromPush();
    setBusy(false);
    setSubscribed(false);
    toast.success("Push turned off · You'll still see in-app signals.");
  };

  const onTest = async () => {
    setBusy(true);
    const r = await sendTestPush();
    setBusy(false);
    if (r.ok && (r.sent || 0) > 0) {
      // The push itself does the OS-level surface.
    } else {
      toast.error("Couldn't dispatch test push. Try re-enabling.");
    }
  };

  if (isEnabled) {
    return (
      <div
        data-testid="push-row-enabled"
        className="px-5 py-3 flex items-center gap-3"
        style={{
          background: "var(--gp-green-soft, #ECFDF5)",
          borderBottom: "1px solid var(--gp-card-border)",
        }}
      >
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
          style={{
            background: "rgba(16,185,129,0.12)",
            color: "#047857",
            border: "1px solid rgba(16,185,129,0.25)",
          }}
        >
          <ShieldCheck size={13} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[12px] font-semibold leading-snug" style={{ color: "var(--gp-text)" }}>
            Push enabled on this device
          </p>
          <p className="text-[11px] mt-0.5" style={{ color: "var(--gp-text-muted)" }}>
            You'll only be notified on operational signals.
          </p>
        </div>
        <button
          onClick={onTest}
          disabled={busy}
          className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg transition-colors hover:bg-white/60 disabled:opacity-60"
          style={{ color: "var(--gp-violet-deep)" }}
          data-testid="push-row-test"
        >
          <Send size={11} /> Test
        </button>
        <button
          onClick={onDisable}
          disabled={busy}
          className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg transition-colors hover:bg-white/60 disabled:opacity-60"
          style={{ color: "var(--gp-text-subtle)" }}
          data-testid="push-row-disable"
        >
          <BellOff size={11} /> Turn off
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="push-row-idle"
      className="px-5 py-3 flex items-center gap-3"
      style={{
        background: "linear-gradient(180deg, rgba(124,58,237,0.045) 0%, rgba(255,255,255,0) 100%)",
        borderBottom: "1px solid var(--gp-card-border)",
      }}
    >
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
        style={{
          background: "var(--gp-violet-soft)",
          color: "var(--gp-violet-deep)",
          border: "1px solid var(--gp-violet-border)",
        }}
      >
        <BellRing size={13} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[12px] font-semibold leading-snug" style={{ color: "var(--gp-text)" }}>
          Enable push for operational alerts
        </p>
        <p className="text-[11px] mt-0.5" style={{ color: "var(--gp-text-muted)" }}>
          Quiet, role-aware. Only when momentum changes.
        </p>
      </div>
      <button
        onClick={onEnable}
        disabled={busy}
        className="text-[11px] font-bold px-3 py-1.5 rounded-lg transition-all disabled:opacity-60"
        style={{
          background: "linear-gradient(135deg, var(--gp-violet-deep) 0%, #4F46E5 100%)",
          color: "#fff",
          boxShadow: "0 4px 12px -3px rgba(99,102,241,0.45)",
        }}
        data-testid="push-row-enable"
      >
        {busy ? "Enabling…" : "Enable"}
      </button>
      <button
        onClick={onDismiss}
        className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors hover:bg-slate-100"
        style={{ color: "var(--gp-text-subtle)" }}
        aria-label="Dismiss"
        data-testid="push-row-dismiss"
      >
        <X size={12} />
      </button>
    </div>
  );
};

export default PushEnablementRow;
